import React, { useState, useEffect } from 'react';
import Layout from '../../../components/layout';
import Seo from '../../../components/seo';
import '../../../styles/trademark-roi-calculator.css';
import Disclaimer from '../../../components/disclaimer';

const TrademarkROICalculator = () => {
  const [activeTab, setActiveTab] = useState('basic');
  
  // Basic Calculator State
  const [basicForm, setBasicForm] = useState({
    trademarkCost: 5000,
    brandValue: 100000,
    revenueIncrease: 15,
    timePeriod: 5
  });
  const [basicResults, setBasicResults] = useState(null);

  // Advanced Calculator State
  const [advancedForm, setAdvancedForm] = useState({
    filingFees: 1500,
    attorneyFees: 3500,
    maintenanceFees: 500,
    enforcementBudget: 2000,
    brandPremium: 20,
    marketShare: 5,
    licensingRevenue: 10000,
    riskReduction: 50000
  });
  const [advancedResults, setAdvancedResults] = useState(null);

  // Portfolio Calculator State
  const [portfolioForm, setPortfolioForm] = useState({
    numMarks: 3,
    totalCost: 15000,
    synergyBonus: 10,
    marketCoverage: 75,
    defensiveValue: 100000,
    licensingPotential: 25000
  });
  const [portfolioResults, setPortfolioResults] = useState(null);

  // SEO data
  const seo = {
    metaDesc: 'Trademark ROI Calculator - Calculate the return on investment for your trademark applications and registrations. Analyze brand value, market protection, and licensing opportunities.'
  };

  // Add no-home class to body on mount
  useEffect(() => {
    document?.body.classList.add('no-home');
    return () => {
      document?.body.classList.remove('no-home');
    };
  }, []);

  // Tab switching function
  const showTab = (tabName) => {
    setActiveTab(tabName);
  };

  // Basic ROI Calculation
  const calculateBasicROI = () => {
    const { trademarkCost, brandValue, revenueIncrease, timePeriod } = basicForm;
    
    const annualBenefit = brandValue * (revenueIncrease / 100);
    const totalBenefit = annualBenefit * timePeriod;
    const roi = ((totalBenefit - trademarkCost) / trademarkCost) * 100;
    const paybackPeriod = trademarkCost / annualBenefit;
    const netValue = totalBenefit - trademarkCost;

    setBasicResults({
      roiPercent: roi.toFixed(1),
      totalBenefit: totalBenefit.toLocaleString(),
      paybackPeriod: paybackPeriod.toFixed(1),
      netValue: netValue.toLocaleString()
    });
  };

  // Advanced ROI Calculation
  const calculateAdvancedROI = () => {
    const {
      filingFees,
      attorneyFees,
      maintenanceFees,
      enforcementBudget,
      brandPremium,
      marketShare,
      licensingRevenue,
      riskReduction
    } = advancedForm;

    const totalInvestment = filingFees + attorneyFees + (maintenanceFees * 5) + enforcementBudget;
    const annualBenefits = licensingRevenue + (riskReduction / 5) + (brandPremium * 1000) + (marketShare * 2000);
    const fiveYearBenefits = annualBenefits * 5;
    const roi = ((fiveYearBenefits - totalInvestment) / totalInvestment) * 100;
    const breakEven = totalInvestment / annualBenefits;

    setAdvancedResults({
      totalInvestment: totalInvestment.toLocaleString(),
      annualBenefits: annualBenefits.toLocaleString(),
      roiPercent: roi.toFixed(1),
      breakEven: breakEven.toFixed(1)
    });
  };

  // Portfolio ROI Calculation
  const calculatePortfolioROI = () => {
    const {
      numMarks,
      totalCost,
      synergyBonus,
      marketCoverage,
      defensiveValue,
      licensingPotential
    } = portfolioForm;

    const costPerMark = totalCost / numMarks;
    const baseValue = licensingPotential * 5 + defensiveValue;
    const synergyValue = baseValue * (synergyBonus / 100);
    const totalValue = baseValue + synergyValue;
    const roiMultiplier = totalValue / totalCost;
    const strategicScore = (marketCoverage + synergyBonus + (numMarks * 10)) / 3;

    setPortfolioResults({
      costPerMark: costPerMark.toLocaleString(),
      totalValue: totalValue.toLocaleString(),
      roiMultiplier: roiMultiplier.toFixed(1),
      strategicScore: strategicScore.toFixed(0)
    });
  };

  // Form update handlers
  const updateBasicForm = (field, value) => {
    setBasicForm(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };

  const updateAdvancedForm = (field, value) => {
    setAdvancedForm(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };

  const updatePortfolioForm = (field, value) => {
    setPortfolioForm(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };

  return (
    // <Layout activeMenu={'ai-tools'}>
    //   <Seo 
    //     title="Trademark ROI Calculator - Calculate Your Trademark Investment Returns" 
    //     canonical='/ai-tools/trademark-roi-calculator' 
    //     seo={seo} 
    //   />
      
      <div className="trademark-roi-calculator">
        <div className="trademark-roi-container">
          <div className="trademark-roi-header">
            <h1>Trademark ROI Calculator</h1>
            <p>Calculate the return on investment for your trademark applications and registrations</p>
          </div>
          
          <div className="trademark-roi-warning-box">
            <h3>⚠️ Important Legal Disclaimer</h3>
            <p>Please note that all assessment results are preliminary and may not be confidential if not using a paid AI account with the proper safeguards so that AI will not train on the data. The app is provided free of charge and there is no attorney client relationship by using this app. Further, proper creation and capture of IP value require tailored legal and valuation strategies—founders should consult a qualified patent attorney at <a href="https://PatentPC.com" target="_blank" rel="noopener noreferrer">PatentPC.com</a> to develop a robust IP portfolio and maximize startup valuation. Please visit <a href="https://PatentPC.com" target="_blank" rel="noopener noreferrer">PatentPC.com</a> to consult with a patent lawyer.</p>
          </div>
          
          <div className="trademark-roi-nav-tabs">
            <div 
              className={`trademark-roi-nav-tab ${activeTab === 'basic' ? 'active' : ''}`}
              onClick={() => showTab('basic')}
            >
              Basic Calculator
            </div>
            <div 
              className={`trademark-roi-nav-tab ${activeTab === 'advanced' ? 'active' : ''}`}
              onClick={() => showTab('advanced')}
            >
              Advanced Analysis
            </div>
            <div 
              className={`trademark-roi-nav-tab ${activeTab === 'portfolio' ? 'active' : ''}`}
              onClick={() => showTab('portfolio')}
            >
              Portfolio Analysis
            </div>
            <div 
              className={`trademark-roi-nav-tab ${activeTab === 'info' ? 'active' : ''}`}
              onClick={() => showTab('info')}
            >
              Information
            </div>
          </div>

          {/* Basic Calculator Tab */}
          <div className={`trademark-roi-tab-content ${activeTab === 'basic' ? 'active' : ''}`}>
            <h2>Basic Trademark ROI Calculator</h2>
            <div className="trademark-roi-form-grid">
              <div className="trademark-roi-form-group">
                <label htmlFor="trademark-cost">Total Trademark Costs ($)</label>
                <input 
                  type="number" 
                  id="trademark-cost" 
                  placeholder="e.g., 5000" 
                  value={basicForm.trademarkCost}
                  onChange={(e) => updateBasicForm('trademarkCost', e.target.value)}
                />
              </div>
              <div className="trademark-roi-form-group">
                <label htmlFor="brand-value">Current Brand Value ($)</label>
                <input 
                  type="number" 
                  id="brand-value" 
                  placeholder="e.g., 100000" 
                  value={basicForm.brandValue}
                  onChange={(e) => updateBasicForm('brandValue', e.target.value)}
                />
              </div>
              <div className="trademark-roi-form-group">
                <label htmlFor="revenue-increase">Annual Revenue Increase (%)</label>
                <input 
                  type="number" 
                  id="revenue-increase" 
                  placeholder="e.g., 15" 
                  value={basicForm.revenueIncrease}
                  onChange={(e) => updateBasicForm('revenueIncrease', e.target.value)}
                />
              </div>
              <div className="trademark-roi-form-group">
                <label htmlFor="time-period">Analysis Period (years)</label>
                <input 
                  type="number" 
                  id="time-period" 
                  placeholder="e.g., 5" 
                  value={basicForm.timePeriod}
                  onChange={(e) => updateBasicForm('timePeriod', e.target.value)}
                />
              </div>
            </div>
            <button className="trademark-roi-calculate-btn" onClick={calculateBasicROI}>
              Calculate ROI
            </button>
            
            {basicResults && (
              <div className="trademark-roi-results-section show">
                <h3>ROI Analysis Results</h3>
                <div className="trademark-roi-results-grid">
                  <div className="trademark-roi-result-item">
                    <div className="trademark-roi-result-value">{basicResults.roiPercent}%</div>
                    <div className="trademark-roi-result-label">ROI Percentage</div>
                  </div>
                  <div className="trademark-roi-result-item">
                    <div className="trademark-roi-result-value">${basicResults.totalBenefit}</div>
                    <div className="trademark-roi-result-label">Total Benefit</div>
                  </div>
                  <div className="trademark-roi-result-item">
                    <div className="trademark-roi-result-value">{basicResults.paybackPeriod} years</div>
                    <div className="trademark-roi-result-label">Payback Period</div>
                  </div>
                  <div className="trademark-roi-result-item">
                    <div className="trademark-roi-result-value">${basicResults.netValue}</div>
                    <div className="trademark-roi-result-label">Net Value Added</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Advanced Analysis Tab */}
          <div className={`trademark-roi-tab-content ${activeTab === 'advanced' ? 'active' : ''}`}>
            <h2>Advanced Trademark Analysis</h2>
            <div className="trademark-roi-form-grid">
              <div className="trademark-roi-form-group">
                <label htmlFor="adv-filing-fees">Filing Fees ($)</label>
                <input 
                  type="number" 
                  id="adv-filing-fees" 
                  placeholder="e.g., 1500" 
                  value={advancedForm.filingFees}
                  onChange={(e) => updateAdvancedForm('filingFees', e.target.value)}
                />
              </div>
              <div className="trademark-roi-form-group">
                <label htmlFor="adv-attorney-fees">Attorney Fees ($)</label>
                <input 
                  type="number" 
                  id="adv-attorney-fees" 
                  placeholder="e.g., 3500" 
                  value={advancedForm.attorneyFees}
                  onChange={(e) => updateAdvancedForm('attorneyFees', e.target.value)}
                />
              </div>
              <div className="trademark-roi-form-group">
                <label htmlFor="adv-maintenance-fees">Annual Maintenance ($)</label>
                <input 
                  type="number" 
                  id="adv-maintenance-fees" 
                  placeholder="e.g., 500" 
                  value={advancedForm.maintenanceFees}
                  onChange={(e) => updateAdvancedForm('maintenanceFees', e.target.value)}
                />
              </div>
              <div className="trademark-roi-form-group">
                <label htmlFor="adv-enforcement-budget">Enforcement Budget ($)</label>
                <input 
                  type="number" 
                  id="adv-enforcement-budget" 
                  placeholder="e.g., 2000" 
                  value={advancedForm.enforcementBudget}
                  onChange={(e) => updateAdvancedForm('enforcementBudget', e.target.value)}
                />
              </div>
              <div className="trademark-roi-form-group">
                <label htmlFor="adv-brand-premium">Brand Premium (%)</label>
                <input 
                  type="number" 
                  id="adv-brand-premium" 
                  placeholder="e.g., 20" 
                  value={advancedForm.brandPremium}
                  onChange={(e) => updateAdvancedForm('brandPremium', e.target.value)}
                />
              </div>
              <div className="trademark-roi-form-group">
                <label htmlFor="adv-market-share">Market Share Increase (%)</label>
                <input 
                  type="number" 
                  id="adv-market-share" 
                  placeholder="e.g., 5" 
                  value={advancedForm.marketShare}
                  onChange={(e) => updateAdvancedForm('marketShare', e.target.value)}
                />
              </div>
              <div className="trademark-roi-form-group">
                <label htmlFor="adv-licensing-revenue">Annual Licensing Revenue ($)</label>
                <input 
                  type="number" 
                  id="adv-licensing-revenue" 
                  placeholder="e.g., 10000" 
                  value={advancedForm.licensingRevenue}
                  onChange={(e) => updateAdvancedForm('licensingRevenue', e.target.value)}
                />
              </div>
              <div className="trademark-roi-form-group">
                <label htmlFor="adv-risk-reduction">Risk Reduction Value ($)</label>
                <input 
                  type="number" 
                  id="adv-risk-reduction" 
                  placeholder="e.g., 50000" 
                  value={advancedForm.riskReduction}
                  onChange={(e) => updateAdvancedForm('riskReduction', e.target.value)}
                />
              </div>
            </div>
            <button className="trademark-roi-calculate-btn" onClick={calculateAdvancedROI}>
              Calculate Advanced ROI
            </button>
            
            {advancedResults && (
              <div className="trademark-roi-results-section show">
                <h3>Advanced ROI Analysis Results</h3>
                <div className="trademark-roi-results-grid">
                  <div className="trademark-roi-result-item">
                    <div className="trademark-roi-result-value">${advancedResults.totalInvestment}</div>
                    <div className="trademark-roi-result-label">Total Investment</div>
                  </div>
                  <div className="trademark-roi-result-item">
                    <div className="trademark-roi-result-value">${advancedResults.annualBenefits}</div>
                    <div className="trademark-roi-result-label">Annual Benefits</div>
                  </div>
                  <div className="trademark-roi-result-item">
                    <div className="trademark-roi-result-value">{advancedResults.roiPercent}%</div>
                    <div className="trademark-roi-result-label">5-Year ROI</div>
                  </div>
                  <div className="trademark-roi-result-item">
                    <div className="trademark-roi-result-value">{advancedResults.breakEven} years</div>
                    <div className="trademark-roi-result-label">Break-Even Period</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Portfolio Analysis Tab */}
          <div className={`trademark-roi-tab-content ${activeTab === 'portfolio' ? 'active' : ''}`}>
            <h2>Trademark Portfolio Analysis</h2>
            <div className="trademark-roi-form-grid">
              <div className="trademark-roi-form-group">
                <label htmlFor="port-num-marks">Number of Trademarks</label>
                <input 
                  type="number" 
                  id="port-num-marks" 
                  placeholder="e.g., 3" 
                  value={portfolioForm.numMarks}
                  onChange={(e) => updatePortfolioForm('numMarks', e.target.value)}
                />
              </div>
              <div className="trademark-roi-form-group">
                <label htmlFor="port-total-cost">Total Portfolio Cost ($)</label>
                <input 
                  type="number" 
                  id="port-total-cost" 
                  placeholder="e.g., 15000" 
                  value={portfolioForm.totalCost}
                  onChange={(e) => updatePortfolioForm('totalCost', e.target.value)}
                />
              </div>
              <div className="trademark-roi-form-group">
                <label htmlFor="port-synergy-bonus">Portfolio Synergy Bonus (%)</label>
                <input 
                  type="number" 
                  id="port-synergy-bonus" 
                  placeholder="e.g., 10" 
                  value={portfolioForm.synergyBonus}
                  onChange={(e) => updatePortfolioForm('synergyBonus', e.target.value)}
                />
              </div>
              <div className="trademark-roi-form-group">
                <label htmlFor="port-market-coverage">Market Coverage (%)</label>
                <input 
                  type="number" 
                  id="port-market-coverage" 
                  placeholder="e.g., 75" 
                  value={portfolioForm.marketCoverage}
                  onChange={(e) => updatePortfolioForm('marketCoverage', e.target.value)}
                />
              </div>
              <div className="trademark-roi-form-group">
                <label htmlFor="port-defensive-value">Defensive Value ($)</label>
                <input 
                  type="number" 
                  id="port-defensive-value" 
                  placeholder="e.g., 100000" 
                  value={portfolioForm.defensiveValue}
                  onChange={(e) => updatePortfolioForm('defensiveValue', e.target.value)}
                />
              </div>
              <div className="trademark-roi-form-group">
                <label htmlFor="port-licensing-potential">Annual Licensing Potential ($)</label>
                <input 
                  type="number" 
                  id="port-licensing-potential" 
                  placeholder="e.g., 25000" 
                  value={portfolioForm.licensingPotential}
                  onChange={(e) => updatePortfolioForm('licensingPotential', e.target.value)}
                />
              </div>
            </div>
            <button className="trademark-roi-calculate-btn" onClick={calculatePortfolioROI}>
              Analyze Portfolio
            </button>
            
            {portfolioResults && (
              <div className="trademark-roi-results-section show">
                <h3>Portfolio Analysis Results</h3>
                <div className="trademark-roi-results-grid">
                  <div className="trademark-roi-result-item">
                    <div className="trademark-roi-result-value">${portfolioResults.costPerMark}</div>
                    <div className="trademark-roi-result-label">Cost per Trademark</div>
                  </div>
                  <div className="trademark-roi-result-item">
                    <div className="trademark-roi-result-value">${portfolioResults.totalValue}</div>
                    <div className="trademark-roi-result-label">Total Portfolio Value</div>
                  </div>
                  <div className="trademark-roi-result-item">
                    <div className="trademark-roi-result-value">{portfolioResults.roiMultiplier}x</div>
                    <div className="trademark-roi-result-label">ROI Multiplier</div>
                  </div>
                  <div className="trademark-roi-result-item">
                    <div className="trademark-roi-result-value">{portfolioResults.strategicScore}/100</div>
                    <div className="trademark-roi-result-label">Strategic Score</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Information Tab */}
          <div className={`trademark-roi-tab-content ${activeTab === 'info' ? 'active' : ''}`}>
            <div className="trademark-roi-warning-box">
              <h3>⚠️ Important Legal Disclaimer</h3>
              <p>Please note that all assessment results are preliminary and may not be confidential if not using a paid AI account with the proper safeguards so that AI will not train on the data. The app is provided free of charge and there is no attorney client relationship by using this app. Further, proper creation and capture of IP value require tailored legal and valuation strategies—founders should consult a qualified patent attorney at <a href="https://PatentPC.com" target="_blank" rel="noopener noreferrer">PatentPC.com</a> to develop a robust IP portfolio and maximize startup valuation. Please visit <a href="https://PatentPC.com" target="_blank" rel="noopener noreferrer">PatentPC.com</a> to consult with a patent lawyer.</p>
            </div>
            
            <div className="trademark-roi-info-section">
              <h3>Understanding Trademark ROI</h3>
              <ul>
                <li>Brand protection and exclusive usage rights</li>
                <li>Increased brand value and customer trust</li>
                <li>Premium pricing opportunities</li>
                <li>Licensing and franchising revenue potential</li>
                <li>Legal protection against infringement</li>
                <li>Enhanced market position and competitive advantage</li>
              </ul>
            </div>
            
            <div className="trademark-roi-info-section">
              <h3>Key Factors in Trademark Valuation</h3>
              <ul>
                <li>Market recognition and brand awareness</li>
                <li>Revenue generation capability</li>
                <li>Geographic coverage and protection scope</li>
                <li>Industry sector and market size</li>
                <li>Enforcement history and strength</li>
                <li>Strategic importance to business operations</li>
              </ul>
            </div>
            
            <div className="trademark-roi-info-section">
              <h3>How to Use This Calculator</h3>
              <ul>
                <li>Start with the Basic Calculator for quick estimates</li>
                <li>Use Advanced Analysis for detailed cost-benefit analysis</li>
                <li>Portfolio Analysis helps evaluate multiple trademarks</li>
                <li>Results are estimates - consult professionals for accuracy</li>
                <li>Consider long-term strategic value beyond immediate ROI</li>
                <li>Factor in industry-specific considerations</li>
              </ul>
            </div>
          </div>

          <Disclaimer />
        </div>
      </div>
    // </Layout>
  );
};

export default TrademarkROICalculator;
